import {
  Box,
  Button,
  FormLabel,
  FormControl,
  TextField,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import {
  getLocalSavedUsername,
  resetLocalSavedUsername,
  setLocalSavedUsername,
} from "~/state/auth/savedUsername";
import { fetchIdpByEmail, initiateLogin } from "~/utils/signIn";
import { AuthCardLayout } from "./AuthCardLayout";
import { IDP_QUERY_PARAM } from "~/state/auth/constants";
import { setLocalPreLoginEmail } from "~/state/auth";

type FormValues = {
  email: string;
  remember: boolean;
};

const emailFieldOptions = {
  required: {
    value: true,
    message: "Email is required",
  },
  pattern: {
    value: /\S+@\S+\.\S+/,
    message: "Please enter a valid email address",
  },
};

export default function SignInScreen() {
  const [searchParams] = useSearchParams();

  const [submitting, setSubmitting] = useState(false);

  const savedEmail = useMemo<string | null>(() => {
    return getLocalSavedUsername();
  }, []);

  const emailFromQuery = searchParams.get("email");
  const hideRememberMe = Boolean(searchParams.get("hide_remember_me"));

  const {
    handleSubmit,
    register,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      email: emailFromQuery || savedEmail || "",
      remember: !!savedEmail,
    },
  });

  const reportError = (message: string) => {
    setSubmitting(false);
    setError("email", {
      type: "custom",
      message,
    });
  };

  const onSubmit = async (values: FormValues) => {
    clearErrors();

    if (values.remember) {
      setLocalSavedUsername(values.email);
    }

    submitEmail(values.email, false);
  };

  const submitEmail = async (email: string, fromQueryParam: boolean) => {
    setSubmitting(true);
    try {
      const idp = await fetchIdpByEmail(email);
      if (idp) {
        // If the IDP query param was set set don't really have an email address to
        // store as the pre log in email just the domain name. Skip storing it here
        // so that later when we come back from the IDP we don't try to verify that
        // it's the same user that came back. 
        if(!fromQueryParam) {
          setLocalPreLoginEmail(email); // Stores the email the user entered into the form so we can make sure we got the same one from the IDP
        }
        initiateLogin({
          idp,
          clientId: searchParams.get("client_id"),
          redirectUri: searchParams.get("redirect_uri"),
          codeChallenge: searchParams.get("code_challenge"),
          state: searchParams.get("state"),
        });
      } else {
        setTimeout(() => {
          reportError(`${fromQueryParam ? "Identity provider" : "Email address"} not recognized`);
        }, 1500);
      }
    } catch (error) {
      console.error(error);
      reportError("An error occurred. Please try again later.");
    }
  };

  const rememberFormValue = watch("remember");

  useEffect(() => {
    if (!rememberFormValue) {
      resetLocalSavedUsername();
    }
  }, [rememberFormValue]);

  useEffect(() => {
    const queryIdp = searchParams.get(IDP_QUERY_PARAM);
    if(queryIdp) {
      submitEmail(`@${queryIdp}`, true)
    }
  }, [])

  return (
    <Box width="100vw">
      <AuthCardLayout>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 2,
            mt: 2,
          }}
        >
          <FormControl fullWidth>
            <FormLabel htmlFor="email">Email</FormLabel>
            <TextField
              id="email"
              type="email"
              placeholder="your@email.com"
              autoComplete="email"
              autoFocus
              fullWidth
              variant="outlined"
              {...register("email", emailFieldOptions)}
              error={!!errors.email}
              helperText={errors.email?.message || ""}
              disabled={submitting}
            />
          </FormControl>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{
              justifyContent: "space-between",
            }}
          >
            {!hideRememberMe && (
            <FormControlLabel
              control={
                <Checkbox
                  {...register("remember")}
                  defaultChecked={!!savedEmail}
                  color="primary"
                  sx={{
                    padding: 0.5,
                    "& .MuiSvgIcon-root": { fontSize: 20 },
                  }}
                />
              }
              label="Remember me"
              disabled={submitting}
              slotProps={{
                typography: {
                  color: "textSecondary",
                },
                }}
              />
            )}
          </Stack>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting}
          >
            Sign in
          </Button>
        </Box>
      </AuthCardLayout>
    </Box>
  );
}
